<template>
	<div>
		<claim-detail-submenu />

		<core-layout-content full>
			<core-layout-headline-and-actions>
				<template slot="headline">{{ $t("transactions") }}</template>
				<template slot="actions">
					<j-button
						v-can="'writeTransactions'"
						type="primary"
						@click="$modal.show('addTransaction')"
					>
						<crm-icon name="add" size="small" color="c5" />
						{{ $t("add_transaction") }}
					</j-button>
				</template>
			</core-layout-headline-and-actions>

			<!-- Data Container -->
			<j-card class="bg5">
				<j-data-table
					:header="header"
					:list="computedtransactions"
					:sortable="false"
					:isLoading="isLoading"
					@sort="onSort($event)"
				>
					<template slot="list" slot-scope="{ item }">
						<td v-for="(column, i) in header" :key="i">
							<template v-if="column.field === 'actions'">
								<crm-icon
									v-can="'writeTransactions'"
									name="edit"
									color="c6"
									class="cu2"
									@click.native="handleUpdate(item)"
								/>
							</template>
							<template v-else>
								<template v-if="column.field === 'transactionNumber'">
									<router-link
										:to="
											`/${
												$route.params.tenantId
											}/transactions/detail/transaction/${item.id}`
										"
									>
										<span> {{ item[column.field] || "---" }}</span>
									</router-link>
								</template>
								<template v-else>
									{{ item[column.field] || "---" }}
								</template>
							</template>
						</td>
					</template>
					<template slot="empty">{{ $t("errors.no_data") }}</template>
				</j-data-table>
			</j-card>

			<!-- Create Transaction -->
			<!-- MOCK: inject custom component for AEG that is based on schema -->
			<!-- Eventually we should have the whole claim.transactions in CMS and have a template for displaying it -->
			<core-modal
				name="addTransaction"
				headline="Transaction"
				:adaptive="false"
				@close="fetchTransactions"
				v-if="
					$store.state.tenantId === 'aeg_uat' || $store.state.tenantId === 'aeg'
				"
			>
				<aeg-add-transaction
					style="overflow-y:scroll; height: 90vh;"
					:claimId="id"
					@done="$modal.hide('addTransaction')"
					@transaction:deleted="$modal.hide('addTransaction')"
				/>
			</core-modal>

			<!-- Other tenants -->
			<core-modal
				v-else
				name="addTransaction"
				:headline="$t('add_transaction')"
			>
				<transaction-create
					:claimId="id"
					@cancel="$modal.hide('addTransaction')"
					@transaction:added="
						$modal.hide('addTransaction'), fetchTransactions()
					"
				/>
			</core-modal>

			<!-- Update Transaction -->
			<!-- MOCK: inject custom component for AEG that is based on schema -->
			<!-- Eventually we should have the whole claim.transactions in CMS and have a template for displaying it -->
			<core-modal
				v-if="
					$store.state.tenantId === 'aeg_uat' || $store.state.tenantId === 'aeg'
				"
				name="updateTransaction"
				headline="Transaction"
				@close="fetchTransactions"
			>
				<aeg-update-transaction
					style="overflow-y:scroll; height: 90vh;"
					:claimId="id"
					:transactionId="transactionToUpdateId"
					@done="$modal.hide('updateTransaction')"
					@transaction:deleted="$modal.hide('updateTransaction')"
				/>
			</core-modal>

			<!-- Other tenants -->
			<core-modal
				v-else
				name="updateTransaction"
				:headline="$t('update_transaction')"
			>
				<transaction-update
					:claimId="id"
					:transactionId="transactionToUpdateId"
					:initialPayload="transactionToUpdatePayload"
					@transaction:updated="
						$modal.hide('updateTransaction'), fetchTransactions()
					"
					@cancel="$modal.hide('updateTransaction')"
				/>
			</core-modal>
		</core-layout-content>
	</div>
</template>

<script>
import CrmIcon from "@/components/CrmIcon/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JPagination from "@/components/JPagination/"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import ClaimDetailSubmenu from "../components/ClaimDetailSubmenu"
import CoreModal from "@/modules/core/components/CoreModal"
import TransactionCreate from "../components/TransactionCreate"
import TransactionUpdate from "../components/TransactionUpdate"
import dayjs from "dayjs"
import api from "../api/"
import get from "lodash.get"

// MOCKED: cms components
import AegUpdateTransaction from "../components/mockedCms/AegUpdateTransaction.vue"
import AegAddTransaction from "../components/mockedCms/AegAddTransaction.vue"

export default {
	components: {
		CrmIcon,
		JButton,
		JCard,
		JDataTable,
		JBaseInputSearch,
		JPagination,
		CorePaginationInfo,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		ClaimDetailSubmenu,
		CoreModal,
		TransactionCreate,
		TransactionUpdate,

		// MOCKED: cms components
		AegUpdateTransaction,
		AegAddTransaction
	},

	props: {
		// From router
		id: String
	},

	data: () => ({
		isLoading: false,
		transactions: [],

		// When user clicks on edit icon, we save this id here
		transactionToUpdateId: null,
		transactionToUpdatePayload: null
	}),

	computed: {
		header() {
			return [
				{ text: this.$t("debitNoteNumber"), field: "transactionNumber" },
				{ text: this.$t("when"), field: "dateTime" },
				{ text: this.$t("amount"), field: "formattedAmount" },
				{ text: this.$t("payment_method"), field: "method" },
				{ text: this.$t("status"), field: "localizedStatus" },
				{ text: this.$t("actions"), field: "actions" }
			]
		},

		computedtransactions() {
			const transactions = this.transactions
			if (!transactions) return []

			// Crunch transactions
			// Sort to compare and order dates correctly
			// Also reverse them so that most recent one is the most top
			return transactions
				.sort((a, b) => {
					let dateA = a.dateTime
					let dateB = b.dateTime
					if (dateA < dateB) return -1
					if (dateA > dateB) return 1
					return 0
				})
				.map(item => ({
					id: item.id,
					dateTime: item.dateTime
						? dayjs(item.dateTime).format(
								this.$store.state.settings.dateTimeFormat
						  )
						: null,
					formattedAmount: item.formattedTransaction,
					amount: item.amount,
					currencyCode: item.currencyCode,
					status: item.status,
					localizedStatus: item.status, // TODO: localized
					method: item.method,
					localizedMethod: item.method
						? this.$t(`payment_methods_enums.${item.method}`)
						: null,
					transactionNumber: item.transactionNumber
				}))
				.reverse()
		}
	},

	methods: {
		async fetchTransactions() {
			this.isLoading = true
			const res = await api.claimTransactions({
				where: { id: this.id }
			})
			this.isLoading = false

			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// Success
			this.transactions = res?.data?.claims?.list?.[0]?.transactions || []
		},

		handleUpdate(item) {
			this.transactionToUpdateId = item.id
			this.transactionToUpdatePayload = {
				amount: item.amount,
				currencyCode: item.currencyCode,
				status: item.status,
				dateTime: item.dateTime,
				method: item.method,
				transactionNumber: item.transactionNumber
			}

			// Open popup
			this.$modal.show("updateTransaction")
		}
	},

	created() {
		this.fetchTransactions()
	}
}
</script>
